// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Merit ladders as the backend resolves them. The UI is a presentation layer: every number here
 * arrives from GET /api/game-merit-ladders already weighted, folded and rounded, and the only
 * thing this module does is pick the row for a weight and format it.
 */

export type MeritLadderKind = 'ladder' | 'perEvent' | 'perAchievement' | 'hazard';

export type MeritLadderRow = {
  // Per position for a ladder game; a single award for the other kinds (negative for a hazard).
  ladder: number[];
  // What the winner collects over the tour.
  winnerTotal: number;
  // All merits the game hands out over the tour; null when occurrences cannot be predicted
  // (achievements, hazards).
  tourTotal: number | null;
};

export type GameMeritLadder = {
  gameDefinitionId: string;
  kind: MeritLadderKind;
  // The ladder is per team position rather than per player.
  perTeam: boolean;
  // Ranked inside the ball once the round has more than one.
  playedPerBall: boolean;
  // The field the ladder is paid off: the biggest ball, or the round.
  fieldSize: number;
  // False for a tour-long game whose ladder is already the tour total.
  paysPerRound: boolean;
  // Keyed by weight percent as a string ("50").
  laddersByWeight: Record<string, MeritLadderRow>;
};

export type MeritLaddersByGameId = Record<string, GameMeritLadder>;

export function meritRowForWeight(
  entry: GameMeritLadder | undefined | null,
  weightPercent: number,
): MeritLadderRow | null {
  return entry?.laddersByWeight[String(weightPercent)] ?? null;
}

export function ladderForWeight(entry: GameMeritLadder | undefined | null, weightPercent: number): number[] {
  return meritRowForWeight(entry, weightPercent)?.ladder ?? [];
}

export function formatMerits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function formatMeritLadder(ladder: number[], separator = ' / '): string {
  return ladder.map(formatMerits).join(separator);
}

/**
 * The top of a ladder that has more rungs than there is room for, and how many it leaves out.
 * A ladder has a rung per player and a round may hold 64, so somewhere the list has to stop; the
 * count is what keeps that from being a silent cut. It takes a rung's place, so the two together
 * never exceed `maxRungs` — and a ladder that fits comes back whole.
 */
export function clipMeritLadder(ladder: number[], maxRungs: number): { shown: number[]; hiddenCount: number } {
  if (ladder.length <= maxRungs) return { shown: ladder, hiddenCount: 0 };
  const shown = ladder.slice(0, Math.max(0, maxRungs - 1));
  return { shown, hiddenCount: ladder.length - shown.length };
}

/**
 * i18n key for the caption under a merit ladder. Shared so the game tile and the game setup
 * modal name the same ladder the same way.
 */
export function meritsCaptionKey(flags: {
  perTeam?: boolean;
  perAchievement?: boolean;
  perEvent?: boolean;
}): string {
  if (flags.perEvent) return 'selectGames.meritsPerEventLabel';
  if (flags.perAchievement) return 'selectGames.meritsPerAchievementLabel';
  if (flags.perTeam) return 'selectGames.meritsPerTeamLabel';
  return 'selectGames.meritsLabel';
}
